/**
 * @file     consentRuntime.ts
 * @readme   ../CLAUDE.md
 * @purpose  Browser side of the cookie choice: reads and writes relta_consent,
 *           pushes the choice to Google consent mode and the Meta Pixel, clears
 *           cookies a withdrawn category left behind, and opens the
 *           preferences panel from anywhere (footer, cookie policy).
 */
'use client';

import { browserGpcEnabled } from './gpc';
import { grantMetaPixel, revokeMetaPixel } from './metaPixel';
import {
  CONSENT_COOKIE,
  consentMaxAgeLeft,
  parseConsent,
  serializeConsent,
  type ConsentChoice,
} from './consent';

export const OPEN_COOKIE_PREFERENCES_EVENT = 'relta:open-cookie-preferences';
export const CONSENT_CHANGED_EVENT = 'relta:consent-changed';

export interface ConsentSelection {
  analytics: boolean;
  marketing: boolean;
}

type ConsentState = 'granted' | 'denied';

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? m[1] : null;
}

export function readStoredConsent(): ConsentChoice | null {
  if (typeof document === 'undefined') return null;
  return parseConsent(readCookie(CONSENT_COOKIE));
}

/** What actually reaches the tags: GPC keeps advertising off whatever was chosen. */
export function effectiveConsent(selection: ConsentSelection, gpc = browserGpcEnabled()): ConsentSelection {
  return { analytics: selection.analytics, marketing: selection.marketing && !gpc };
}

export function googleConsentUpdate(selection: ConsentSelection): Record<string, ConsentState> {
  const a: ConsentState = selection.analytics ? 'granted' : 'denied';
  const m: ConsentState = selection.marketing ? 'granted' : 'denied';
  return { analytics_storage: a, ad_storage: m, ad_user_data: m, ad_personalization: m };
}

const ANALYTICS_COOKIES = [/^_ga$/, /^_ga_/, /^_gid$/, /^relta_cid$/];
const MARKETING_COOKIES = [/^_gcl_/, /^_fbp$/, /^_fbc$/];

/**
 * Google and Meta write on the registrable domain (.relta.ca), relta_cid on
 * the host, so each name is expired on both.
 */
function expireCookies(patterns: RegExp[]): void {
  const names = document.cookie
    .split('; ')
    .map((c) => c.split('=')[0])
    .filter((n) => patterns.some((p) => p.test(n)));
  if (names.length === 0) return;
  const host = location.hostname;
  const parts = host.split('.');
  const domains = parts.length >= 2 ? [`.${parts.slice(-2).join('.')}`, `.${host}`] : [];
  for (const name of names) {
    document.cookie = `${name}=; Max-Age=0; Path=/`;
    for (const d of domains) document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${d}`;
  }
}

/** Push a choice to the live tags without storing it. */
export function applyConsent(selection: ConsentSelection): void {
  const effective = effectiveConsent(selection);
  (window as { gtag?: (...args: unknown[]) => void }).gtag?.('consent', 'update', googleConsentUpdate(effective));
  if (effective.marketing) grantMetaPixel();
  else revokeMetaPixel();
  if (!effective.analytics) expireCookies(ANALYTICS_COOKIES);
  if (!effective.marketing) expireCookies(MARKETING_COOKIES);
}

/**
 * Store the choice for 12 months and apply it. Script-written cookies are
 * capped at seven days by Safari, so middleware re-issues it over HTTP on the
 * next page request (see attributionCookies.ts).
 */
export function saveConsent(selection: ConsentSelection): ConsentChoice {
  const decidedAt = Math.floor(Date.now() / 1000);
  const value = serializeConsent({ ...selection, decidedAt });
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${CONSENT_COOKIE}=${value}; Max-Age=${consentMaxAgeLeft(decidedAt, decidedAt)}; Path=/; SameSite=Lax${secure}`;
  applyConsent(selection);
  window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
  return { ...selection, decidedAt, serverStamped: false };
}

export function openCookiePreferences(): void {
  window.dispatchEvent(new Event(OPEN_COOKIE_PREFERENCES_EVENT));
}
